// src/components/invoices/InvoiceDetails.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./invoices.css";
import { fetchBillDetails, saveBill, deleteBill } from "../../services/invoices";


/* -------- helpers -------- */
function safeJson(v, fallback) {
    try {
        const s = typeof v === "string" ? v.trim() : v;
        return JSON.parse(s || "");
    } catch {
        return fallback;
    }
}

function pad2(n) {
    const s = String(n);
    return s.length === 1 ? `0${s}` : s;
}

// accepts yyyy-MM-dd OR dd/MM/yyyy
function toDdMM(s) {
    const x = String(s || "").trim();
    if (!x) return "";
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(x)) return x;
    if (/^\d{4}-\d{2}-\d{2}$/.test(x)) {
        const [y, m, d] = x.split("-");
        return `${pad2(d)}/${pad2(m)}/${y}`;
    }
    return x;
}

function parseAnyDateToObj(s) {
    const x = String(s || "").trim();
    if (!x) return null;

    // dd/MM/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(x)) {
        const [dd, mm, yy] = x.split("/");
        const dt = new Date(Number(yy), Number(mm) - 1, Number(dd));
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // yyyy-MM-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(x)) {
        const [y, m, d] = x.split("-").map((k) => parseInt(k, 10));
        const dt = new Date(y, (m || 1) - 1, d || 1);
        return Number.isNaN(dt.getTime()) ? null : dt;
    }

    // fallback
    const dt = new Date(x);
    return Number.isNaN(dt.getTime()) ? null : dt;
}

function toIsoYmd(s) {
    const dt = parseAnyDateToObj(s);
    if (!dt) return String(s || "").trim();
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function diffDaysInclusive(d1, d2) {
    const a = parseAnyDateToObj(d1);
    const b = parseAnyDateToObj(d2);
    if (!a || !b) return 0;
    const A = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const B = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    const ms = B.getTime() - A.getTime();
    const days = Math.floor(ms / (24 * 60 * 60 * 1000));
    return Math.max(0, days) + 1;
}

function moneyINR(x) {
    const n = Number(x || 0);
    const s = Math.abs(n).toFixed(2);
    return n < 0 ? `-₹${s}` : `₹${s}`;
}

/* -------- JSON extractors -------- */
function pickString(o, ...keys) {
    if (!o) return "";
    for (const k of keys) {
        const v = String(o?.[k] ?? "").trim();
        if (v) return v;
    }
    return "";
}

function pickNum(o, ...keys) {
    if (!o) return 0;
    for (const k of keys) {
        if (o?.[k] == null) continue;
        const n = Number(String(o[k]).replace(/[^0-9.-]/g, ""));
        if (!Number.isNaN(n)) return n;
    }
    return 0;
}

function pickSectionArray(root, sectionNames, rowKeys) {
    for (const sec of sectionNames) {
        const v = root?.[sec];
        if (Array.isArray(v)) return v;
        if (v && typeof v === "object") {
            for (const rk of rowKeys) {
                if (Array.isArray(v?.[rk])) return v[rk];
            }
        }
    }
    return [];
}

function normalizeMilkTypeKey(raw) {
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return "";
    if (s === "b" || s.startsWith("buffalo") || s.includes("भैंस") || s.includes("भैस") || s.includes("म्है"))
        return "buffalo";
    if (s === "c" || s.startsWith("cow") || s.includes("गाय") || s.includes("गाई") || s.includes("हಸು"))
        return "cow";
    return s;
}

function ssnKey(raw) {
    const s = String(raw || "").trim().toLowerCase();
    if (!s) return "";
    if (s.includes("mor") || s.includes("सक") || s.includes("sakal")) return "morning";
    if (s.includes("eve") || s.includes("even") || s.includes("सं") || s.includes("sand")) return "evening";
    return s;
}

function ssnLabel(key) {
    if (key === "morning") return "Morning";
    if (key === "evening") return "Evening";
    return key || "";
}

function typeLabel(key) {
    if (key === "buffalo") return "Buffalo";
    if (key === "cow") return "Cow";
    return key || "";
}

function ssnOrder(key) {
    if (key === "morning") return 0;
    if (key === "evening") return 1;
    return 2;
}
function typeOrder(key) {
    if (key === "buffalo") return 0;
    if (key === "cow") return 1;
    return 2;
}



export default function InvoiceDetails({
    mainuser,
    kno,
    date1, // ISO or dd/MM
    date2, // ISO or dd/MM
    source, // "pending" or "saved"
    onBack,

    // right-top nav like screenshot: "Invoices ( 1 / 2 )" + next button
    indexLabel, // e.g. "1 / 2"
    onPrev,
    onNext,

    // optional action hooks
    onEditCollections,
    onEditFeeds,
    onSaveInvoice,
}) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [root, setRoot] = useState(null);
    const reqSeqRef = useRef(0);

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // local override after save (so UI becomes saved without changing design)
    const [savedLocal, setSavedLocal] = useState(false);

    const d1Send = useMemo(() => toDdMM(date1), [date1]);
    const d2Send = useMemo(() => toDdMM(date2), [date2]);

    const isSavedMode = (source === "saved") || savedLocal;

    const farmerTitle = useMemo(() => {
        const r = root || {};
        const hdr = r?.header || {};
        const name = pickString(hdr, "cname", "name", "customer") || pickString(r, "cname", "name");
        const code = pickString(hdr, "kno", "cust_no", "no") || String(kno || "");
        return `Farmer : ${code}${name ? ` - ${name}` : ""}`;
    }, [root, kno]);

    const dateLine = useMemo(() => `Date : ${d1Send} To ${d2Send}`, [d1Send, d2Send]);

    async function loadDetails() {
        if (!mainuser || !kno || !d1Send || !d2Send) return;

        const mySeq = ++reqSeqRef.current;
        setLoading(true);
        setErr("");

        try {
            const resp = await fetchBillDetails(mainuser, kno, d1Send, d2Send, {
                etag: null,
                source: (source === "saved" || savedLocal) ? "saved" : undefined,
            });

            if (mySeq !== reqSeqRef.current) return;

            const json = safeJson(resp.text, null);
            if (!json) {
                setRoot(null);
                setErr("No data");
                return;
            }
            setRoot(json);

            // server-aware saved flag (like Android "serverSaysSaved")
            const hdr = json?.header || {};
            const serverSaysSaved =
                Boolean(json?.saved) ||
                String(json?.status || "").toLowerCase() === "saved" ||
                Boolean(hdr?.saved) ||
                String(hdr?.status || "").toLowerCase() === "saved" ||
                hdr?.saved_on != null ||
                hdr?.savedAt != null ||
                hdr?.billno != null ||
                hdr?.bill_id != null;

            if (serverSaysSaved) setSavedLocal(true);
        } catch (e) {
            if (mySeq !== reqSeqRef.current) return;
            setErr(e?.message || "Network Error");
            setRoot(null);
        } finally {
            if (mySeq === reqSeqRef.current) setLoading(false);
        }
    }

    useEffect(() => {
        loadDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainuser, kno, d1Send, d2Send, source]);

    /* ---------- Collections rows ---------- */
    const sankRowsAll = useMemo(() => {
        const r = root || {};
        const arr = pickSectionArray(r, ["sank", "sanklan", "milk", "dugh", "dudh"], ["rows", "data", "items"]);

        const out = (Array.isArray(arr) ? arr : []).map((o) => {
            const dayRaw = pickString(o, "date", "sdate", "dt");
            let day = dayRaw;
            try {
                if (/^\d{4}-\d{2}-\d{2}/.test(dayRaw)) day = pad2(Number(dayRaw.slice(8, 10)));
                else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dayRaw)) day = dayRaw.slice(0, 2);
            } catch { }

            const ssn = ssnKey(pickString(o, "time", "vibhag", "session", "ssn"));
            const typ = normalizeMilkTypeKey(pickString(o, "type", "dudh", "milk_type"));

            return {
                day,
                ssn,
                type: typ,
                qty: pickNum(o, "qty", "liter", "ltr", "litre", "liters"),
                fat: pickNum(o, "fat", "FAT"),
                snf: pickNum(o, "snf", "SNF"),
                clr: pickNum(o, "clr", "CLR"),
                rate: pickNum(o, "rate", "dar", "price"),
                amt: pickNum(o, "amt", "amount", "rakam", "total"),
            };
        });

        // sort: date -> morning/evening -> buffalo/cow
        out.sort((a, b) => {
            const d = String(a.day).localeCompare(String(b.day));
            if (d !== 0) return d;
            const s = ssnOrder(a.ssn) - ssnOrder(b.ssn);
            if (s !== 0) return s;
            const t = typeOrder(a.type) - typeOrder(b.type);
            if (t !== 0) return t;
            return 0;
        });

        return out;
    }, [root]);

    // tabs like screenshot
    const [tab, setTab] = useState("all"); // all | morning | evening
    useEffect(() => setTab("all"), [kno, d1Send, d2Send]);

    const sankRows = useMemo(() => {
        if (tab === "morning") return sankRowsAll.filter((x) => x.ssn === "morning");
        if (tab === "evening") return sankRowsAll.filter((x) => x.ssn === "evening");
        return sankRowsAll;
    }, [sankRowsAll, tab]);

    const totalsRow = useMemo(() => {
        let qty = 0,
            amt = 0,
            fatNum = 0,
            snfNum = 0;

        for (const r of sankRows) {
            qty += r.qty || 0;
            amt += r.amt || 0;
            fatNum += (r.qty || 0) * (r.fat || 0);
            snfNum += (r.qty || 0) * (r.snf || 0);
        }

        const fatAvg = qty > 0 ? fatNum / qty : 0;
        const snfAvg = qty > 0 ? snfNum / qty : 0;
        const rateAvg = qty > 0 ? amt / qty : 0;

        return { qty, fatAvg, snfAvg, rateAvg, amt };
    }, [sankRows]);

    /* ---------- Right milk summary (Buffalo/Cow/Total) ---------- */
    const sankSummary = useMemo(() => {
        const sumFor = (typeKey) => {
            let ltr = 0,
                fatNum = 0,
                snfNum = 0,
                amt = 0;
            for (const r of sankRowsAll) {
                if (r.type !== typeKey) continue;
                ltr += r.qty;
                fatNum += r.qty * r.fat;
                snfNum += r.qty * r.snf;
                amt += r.amt;
            }
            const fatAvg = ltr > 0 ? fatNum / ltr : 0;
            const snfAvg = ltr > 0 ? snfNum / ltr : 0;
            const rateAvg = ltr > 0 ? amt / ltr : 0;
            return { ltr, fatAvg, snfAvg, rateAvg, amt };
        };

        const b = sumFor("buffalo");
        const c = sumFor("cow");
        const total = {
            ltr: b.ltr + c.ltr,
            fatAvg: b.ltr + c.ltr > 0 ? (b.fatAvg * b.ltr + c.fatAvg * c.ltr) / (b.ltr + c.ltr) : 0,
            snfAvg: b.ltr + c.ltr > 0 ? (b.snfAvg * b.ltr + c.snfAvg * c.ltr) / (b.ltr + c.ltr) : 0,
            rateAvg: b.ltr + c.ltr > 0 ? (b.amt + c.amt) / (b.ltr + c.ltr) : 0,
            amt: b.amt + c.amt,
        };
        return { buffalo: b, cow: c, total };
    }, [sankRowsAll]);

    /* ---------- ✅ Cattle Feeds (Khadyasale) ---------- */
    const khRows = useMemo(() => {
        const r = root || {};
        const arr = pickSectionArray(r, ["khadyasale", "kh", "khadya", "fodder"], ["rows", "data", "items"]);

        return (Array.isArray(arr) ? arr : []).map((o) => {
            const dayRaw = pickString(o, "date", "dt");
            let day = dayRaw;
            try {
                if (/^\d{4}-\d{2}-\d{2}/.test(dayRaw)) day = pad2(Number(dayRaw.slice(8, 10)));
                else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dayRaw)) day = dayRaw.slice(0, 2);
            } catch { }

            return {
                day,
                name: pickString(o, "khname", "name", "item", "pname", "title"),
                qty: pickNum(o, "qty", "quantity", "count", "sankhya", "quin"),
                rate: pickNum(o, "rate", "price", "dar"),
                amt: pickNum(o, "amt", "amount", "rakam", "total"),
            };
        });
    }, [root]);

    const khTotals = useMemo(() => {
        let qty = 0,
            amt = 0,
            rateNum = 0;
        for (const r of khRows) {
            qty += r.qty || 0;
            amt += r.amt || 0;
            rateNum += r.rate || 0;
        }
        const rateAvg = khRows.length ? rateNum / khRows.length : 0;
        return { qty, rateAvg, amt };
    }, [khRows]);

    /* ---------- Deductions ---------- */
    const kapatRows = useMemo(() => {
        const r = root || {};
        const arr = pickSectionArray(r, ["kapat", "kapat_items", "deductions"], ["rows", "data", "items"]);
        return (Array.isArray(arr) ? arr : []).map((o) => ({
            name: pickString(o, "name", "title", "label", "namemarathi"),
            cut: pickNum(o, "cutnow", "cut_now", "amt", "amount", "rakam"),
            balance: pickNum(o, "balance", "bal", "shillak"),
            deposit: pickNum(o, "cr", "jama", "deposit", "paid"),
        }));
    }, [root]);

    const kapatTotals = useMemo(() => {
        let cut = 0,
            bal = 0,
            dep = 0;
        for (const k of kapatRows) {
            cut += k.cut || 0;
            bal += k.balance || 0;
            dep += k.deposit || 0;
        }
        return { cut, bal, dep };
    }, [kapatRows]);

    /* ---------- Invoice summary card ---------- */
    const summaryBox = useMemo(() => {
        const r = root || {};
        const s = r?.summary || {};
        const milkAmt = pickNum(s, "milk_amt", "milkAmount", "milk") || sankSummary.total.amt || 0;
        const add = pickNum(s, "addl_dey", "additions", "add");
        const gross = pickNum(s, "gross", "gross_amt", "grossAmount") || milkAmt + add;
        const kapat = pickNum(s, "kapat", "kapat_cut", "total_deduction") || kapatTotals.cut || 0;
        const payable = pickNum(s, "payable", "overall_payable") || Math.max(0, gross - kapat);

        // balance (red line in screenshot)
        const bal = pickNum(r, "overall_bal", "balance", "overall_balance") || pickNum(s, "balance") || 0;

        return { milkAmt, add, gross, kapat, payable, balance: Math.abs(bal) };
    }, [root, sankSummary, kapatTotals]);

    /* ---------- Present Shift : x/y ---------- */
    const presentShift = useMemo(() => {
        const days = diffDaysInclusive(d1Send, d2Send);
        const expected = days > 0 ? days * 2 : 0;
        const present = sankRowsAll.length;
        return `${present}${expected ? `/${expected}` : ""}`;
    }, [d1Send, d2Send, sankRowsAll]);

    const paymentReceived = useMemo(() => {
        const r = root || {};
        return pickNum(r, "payment_received", "paymentReceived", "paid", "total_paid");
    }, [root]);

    const showSave = !isSavedMode;
    const showDelete = isSavedMode;

    async function handleSaveInvoice() {
        if (saving) return;
        if (!mainuser) return;
        if (!root) {
            alert("No data to save.");
            return;
        }

        if (onSaveInvoice) {
            try {
                const maybe = await onSaveInvoice();
                if (maybe === true) return;
            } catch { }
        }

        const ok = window.confirm("Do you want to save this invoice?");
        if (!ok) return;

        setSaving(true);
        try {
            const payloadJson = JSON.stringify(root);
            const text = await saveBill(mainuser, payloadJson);

            const j = safeJson(text, null);
            const success =
                (j && (j.ok === true || j.success === true)) ||
                text.includes('"ok":true') ||
                text.toLowerCase().includes("success");

            if (!success) {
                alert("Save failed. Please check server response.");
                return;
            }

            setSavedLocal(true);
            loadDetails(); // optional refresh
            alert("Invoice saved successfully.");
        } catch (e) {
            alert(e?.message || "Network Error");
        } finally {
            setSaving(false);
        }
    }

    async function handleDeleteInvoice() {
        if (deleting) return;
        if (!mainuser || !kno) return;

        const ok = window.confirm("Do you want to delete this invoice?");
        if (!ok) return;

        setDeleting(true);
        try {
            const period_start = toIsoYmd(date1);
            const period_end = toIsoYmd(date2);

            const text = await deleteBill(mainuser, period_start, period_end, String(kno));

            const j = safeJson(text, null);
            const deleted =
                (j && (j.deleted === true || j.ok === true)) ||
                (text.includes('"ok":true') && text.includes('"deleted":true'));

            if (!deleted) {
                alert("Delete failed. Please check server response.");
                return;
            }

            alert("Invoice deleted successfully.");
            if (onBack) onBack();
        } catch (e) {
            alert(e?.message || "Network Error");
        } finally {
            setDeleting(false);
        }
    }

    return (
        <div className="invDetWrap">
            {/* ✅ Back button ABOVE Farmer row */}
            {onBack ? (
                <div className="invDetBackBar">
                    <button type="button" className="invDetBackTopBtn" onClick={onBack} title="Back">
                        ←
                    </button>
                </div>
            ) : null}

            {/* Header EXACT like screenshot (no big title bar) */}
            <div className="invDetTopRow">
                <div className="invDetFarmer">{farmerTitle}</div>

                <div className="invDetNav">
                    <button
                        type="button"
                        className="invDetNavBtn"
                        onClick={() => (onPrev ? onPrev() : null)}
                        disabled={!onPrev}
                        title="Previous"
                    >
                        ‹
                    </button>

                    <div className="invDetNavText">Invoices ( {indexLabel || ""} )</div>

                    <button
                        type="button"
                        className="invDetNavBtn"
                        onClick={() => (onNext ? onNext() : null)}
                        disabled={!onNext}
                        title="Next"
                    >
                        ›
                    </button>
                </div>
            </div>

            <div className="invDetDateRow">{dateLine}</div>
            <div className="invDetHr" />

            {err ? <div className="invMsg err">{err}</div> : null}
            {loading ? <div className="invMsg">Loading...</div> : null}

            <div className="invDetGrid">
                {/* LEFT */}
                <div className="invDetLeft">
                    {/* Collections Card */}
                    <div className="invDetCard">
                        <div className="invDetTabs">
                            <button
                                type="button"
                                className={`invDetTab ${tab === "all" ? "active" : ""}`}
                                onClick={() => setTab("all")}
                            >
                                Collections
                            </button>

                            <button
                                type="button"
                                className={`invDetTab ${tab === "morning" ? "active" : ""}`}
                                onClick={() => setTab("morning")}
                            >
                                <span className="invDetTabIcon">☀</span> Morn
                            </button>

                            <button
                                type="button"
                                className={`invDetTab ${tab === "evening" ? "active" : ""}`}
                                onClick={() => setTab("evening")}
                            >
                                <span className="invDetTabIcon">☾</span> Even
                            </button>

                            <div className="invDetPresent">Present Shift : {presentShift}</div>

                            <button
                                type="button"
                                className="invDetEdit"
                                onClick={() => (onEditCollections ? onEditCollections() : null)}
                            >
                                ✎ <span>Edit</span>
                            </button>
                        </div>

                        <div className="invTableWrap">
                            <table className="invTable invDetCollectionsTable">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Shift</th>
                                        <th>Milk</th>
                                        <th className="num">Quantity</th>
                                        <th className="num">FAT</th>
                                        <th className="num">SNF</th>
                                        <th className="num">CLR</th>
                                        <th className="num">Rate</th>
                                        <th className="num">Amount</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {sankRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="invNoRecords">
                                                No Records Found.
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {sankRows.map((r, idx) => {
                                                const qtyClass = r.qty >= 10 ? "qtyGreen" : r.qty <= 1 ? "qtyRed" : "";
                                                return (
                                                    <tr key={`${idx}-${r.day}-${r.ssn}-${r.type}`}>
                                                        <td>{r.day}</td>
                                                        <td>{ssnLabel(r.ssn)}</td>
                                                        <td>{typeLabel(r.type)}</td>
                                                        <td className={`num ${qtyClass}`}>{Number(r.qty || 0).toFixed(2)}</td>
                                                        <td className="num">{Number(r.fat || 0).toFixed(1)}</td>
                                                        <td className="num">{Number(r.snf || 0).toFixed(1)}</td>
                                                        <td className="num">{Number(r.clr || 0).toFixed(1)}</td>
                                                        <td className="num">{Number(r.rate || 0).toFixed(2)}</td>
                                                        <td className="num">{Number(r.amt || 0).toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })}

                                            <tr className="invDetTotalRow">
                                                <td>Total</td>
                                                <td>-</td>
                                                <td>-</td>
                                                <td className="num">{totalsRow.qty.toFixed(2)}</td>
                                                <td className="num">{totalsRow.fatAvg.toFixed(1)}</td>
                                                <td className="num">{totalsRow.snfAvg.toFixed(1)}</td>
                                                <td className="num">0.0</td>
                                                <td className="num">{totalsRow.rateAvg.toFixed(2)}</td>
                                                <td className="num">{totalsRow.amt.toFixed(2)}</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ✅ Cattle Feeds Card (NOW shows Khadyasale table) */}
                    <div className="invDetCard" style={{ marginTop: 14 }}>
                        <div className="invDetCardHead">
                            <div className="invDetCardTitle">Cattle Feeds</div>
                            <button type="button" className="invDetEdit" onClick={() => (onEditFeeds ? onEditFeeds() : null)}>
                                ✎ <span>Edit</span>
                            </button>
                        </div>

                        {khRows.length === 0 ? (
                            <div className="invDetEmptyBox">No Records Found.</div>
                        ) : (
                            <div className="invTableWrap">
                                <table className="invTable">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Feed Name</th>
                                            <th className="num">Qty</th>
                                            <th className="num">Rate</th>
                                            <th className="num">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {khRows.map((r, idx) => (
                                            <tr key={`${idx}-${r.day}-${r.name}`}>
                                                <td>{r.day}</td>
                                                <td>{r.name}</td>
                                                <td className="num">{Number(r.qty || 0).toFixed(2)}</td>
                                                <td className="num">{Number(r.rate || 0).toFixed(2)}</td>
                                                <td className="num">{Number(r.amt || 0).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                        <tr className="invDetTotalRow">
                                            <td>Total</td>
                                            <td>-</td>
                                            <td className="num">{khTotals.qty.toFixed(2)}</td>
                                            <td className="num">{khTotals.rateAvg.toFixed(2)}</td>
                                            <td className="num">{khTotals.amt.toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="invDetPaymentRow">
                            <span>Payment Received : {Number(paymentReceived || 0).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Deductions Card */}
                    <div className="invDetCard" style={{ marginTop: 14 }}>
                        <div className="invDetCardHead">
                            <div className="invDetCardTitle purple">Deductions</div>
                        </div>

                        <div className="invTableWrap">
                            <table className="invTable invDetDedTable">
                                <thead>
                                    <tr>
                                        <th>Deduction Type</th>
                                        <th className="num">Deduction</th>
                                        <th className="num">Balance</th>
                                        <th className="num">Deposit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kapatRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="invNoRecords">
                                                No Records Found.
                                            </td>
                                        </tr>
                                    ) : (
                                        <>
                                            {kapatRows.map((k, idx) => (
                                                <tr key={`${idx}-${k.name}`}>
                                                    <td>{k.name}</td>
                                                    <td className="num">{Number(k.cut || 0).toFixed(2)}</td>
                                                    <td className="num">{k.balance ? Number(k.balance).toFixed(2) : "-"}</td>
                                                    <td className="num">{k.deposit ? Number(k.deposit).toFixed(2) : "-"}</td>
                                                </tr>
                                            ))}

                                            <tr className="invDetTotalRow">
                                                <td>Total</td>
                                                <td className="num">{kapatTotals.cut.toFixed(2)}</td>
                                                <td className="num">{kapatTotals.bal ? kapatTotals.bal.toFixed(2) : "-"}</td>
                                                <td className="num">{kapatTotals.dep.toFixed(2)}</td>
                                            </tr>
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* RIGHT */}
                <div className="invDetRight">
                    {/* Milk summary table */}
                    <div className="invDetCard">
                        <div className="invTableWrap">
                            <table className="invTable invDetMilkTable">
                                <thead>
                                    <tr>
                                        <th>Milk</th>
                                        <th className="num">Quantity</th>
                                        <th className="num">FAT</th>
                                        <th className="num">SNF</th>
                                        <th className="num">CLR</th>
                                        <th className="num">Rate</th>
                                        <th className="num">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Buffalo</td>
                                        <td className="num">{sankSummary.buffalo.ltr.toFixed(2)}</td>
                                        <td className="num">{sankSummary.buffalo.fatAvg.toFixed(1)}</td>
                                        <td className="num">{sankSummary.buffalo.snfAvg.toFixed(1)}</td>
                                        <td className="num">0.0</td>
                                        <td className="num">{sankSummary.buffalo.rateAvg.toFixed(2)}</td>
                                        <td className="num">{sankSummary.buffalo.amt.toFixed(2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Cow</td>
                                        <td className="num">{sankSummary.cow.ltr.toFixed(2)}</td>
                                        <td className="num">{sankSummary.cow.fatAvg.toFixed(1)}</td>
                                        <td className="num">{sankSummary.cow.snfAvg.toFixed(1)}</td>
                                        <td className="num">0.0</td>
                                        <td className="num">{sankSummary.cow.rateAvg.toFixed(2)}</td>
                                        <td className="num">{sankSummary.cow.amt.toFixed(2)}</td>
                                    </tr>
                                    <tr className="invDetTotalRow">
                                        <td>Total</td>
                                        <td className="num">{sankSummary.total.ltr.toFixed(2)}</td>
                                        <td className="num">{sankSummary.total.fatAvg.toFixed(1)}</td>
                                        <td className="num">{sankSummary.total.snfAvg.toFixed(1)}</td>
                                        <td className="num">0.0</td>
                                        <td className="num">{sankSummary.total.rateAvg.toFixed(2)}</td>
                                        <td className="num">{sankSummary.total.amt.toFixed(2)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Invoice Summary box EXACT style */}
                    <div className="invDetSummaryBox">
                        <div className="invDetSummaryTitle">Invoice Summary</div>

                        <div className="invDetSummaryRows">
                            <div>Total Milk Amount</div>
                            <div className="num">{summaryBox.milkAmt.toFixed(2)}</div>

                            <div>Total Additions</div>
                            <div className="num">{summaryBox.add.toFixed(2)}</div>

                            <div className="invDetDivider" />

                            <div>Total Amount</div>
                            <div className="num">{summaryBox.gross.toFixed(2)}</div>

                            <div>Total Deduction</div>
                            <div className="num">{summaryBox.kapat.toFixed(2)}</div>
                        </div>

                        <div className="invDetDivider" />

                        <div className="invDetDueRow">
                            <div className="invDetDueLabel">Amount Due</div>
                            <div className="invDetDueValue">{moneyINR(summaryBox.payable)}</div>
                        </div>

                        <div className="invDetDivider" />

                        <div className="invDetBalanceRow">
                            <div className="invDetBalLabel">Balance</div>
                            <div className="invDetBalValue">{summaryBox.balance.toFixed(2)}</div>
                        </div>
                    </div>

                    {/* ✅ Save / Delete button (same place, same design row) */}
                    <div className="invDetSaveRow">
                        {showSave ? (
                            <button
                                type="button"
                                className="invDetSaveBtn"
                                onClick={handleSaveInvoice}
                                disabled={saving}
                                title="Save Invoice"
                            >
                                💾 {saving ? "Saving..." : "Save Invoice"}
                            </button>
                        ) : null}

                        {showDelete ? (
                            <button
                                type="button"
                                className="invDetSaveBtn"
                                onClick={handleDeleteInvoice}
                                disabled={deleting}
                                title="Delete Invoice"
                                style={{ marginLeft: showSave ? 10 : 0 }}
                            >
                                🗑 {deleting ? "Deleting..." : "Delete Invoice"}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
